import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, FileText, Download, RotateCcw, Info, Clock, X, AlertCircle, Loader, Moon, Sun, Keyboard } from 'lucide-react';

// --- Normalización y búsqueda ---

const normalizeText = (text) =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '');

const buildAccentInsensitivePattern = (query) => {
  const normalized = normalizeText(query);
  const accentMap = { a: '[aáà]', e: '[eéè]', i: '[iíì]', o: '[oóò]', u: '[uúùü]' };
  let pattern = '';
  for (const char of normalized) {
    const lc = char.toLowerCase();
    pattern += accentMap[lc] !== undefined
      ? accentMap[lc]
      : char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  return pattern;
};

const parseSearchQuery = (query) => {
  const exactPhrases = [...query.matchAll(/"(.*?)"/g)].map(m => m[1]);
  const cleanQuery = query.replace(/"(.*?)"/g, '');
  const mustInclude = [], mustExclude = [], orTerms = [];

  for (const term of cleanQuery.split(/\s+/).filter(Boolean)) {
    if (term.startsWith('+'))      mustInclude.push(normalizeText(term.slice(1)));
    else if (term.startsWith('-')) mustExclude.push(normalizeText(term.slice(1)));
    else                           orTerms.push(normalizeText(term.toLowerCase()));
  }

  return { exactPhrases, mustInclude, mustExclude, orTerms };
};

// Compila TODOS los patrones una sola vez antes del loop de búsqueda.
// Evita ~63.000 compilaciones de RegExp por búsqueda.
const compilePatterns = ({ exactPhrases, mustInclude, mustExclude, orTerms }) => ({
  exactRegexes:   exactPhrases.map(p => new RegExp(buildAccentInsensitivePattern(p.toLowerCase()), 'i')),
  includeRegexes: mustInclude.map(t  => new RegExp(buildAccentInsensitivePattern(t), 'i')),
  excludeRegexes: mustExclude.map(t  => new RegExp(buildAccentInsensitivePattern(t), 'i')),
  orRegexes:      orTerms.map(t      => new RegExp(buildAccentInsensitivePattern(t), 'i')),
});

// Match rápido con patrones pre-compilados. Se llama ~21.000 veces por búsqueda.
const matchesFast = (text, { exactRegexes, includeRegexes, excludeRegexes, orRegexes }) => {
  const norm = normalizeText(text.toLowerCase());
  for (const rx of excludeRegexes) if (rx.test(norm))  return false;
  for (const rx of exactRegexes)   if (!rx.test(norm)) return false;
  for (const rx of includeRegexes) if (!rx.test(norm)) return false;
  if (!exactRegexes.length && !includeRegexes.length) {
    if (!orRegexes.length) return false;
    if (!orRegexes.some(rx => rx.test(norm))) return false;
  }
  return true;
};

// Resaltado con patrones pre-compilados (reutiliza source para agregar flag 'g')
const highlightFast = (text, compiled, searchCriteria) => {
  let out = text;
  const { exactRegexes, includeRegexes, orRegexes } = compiled;
  const { exactPhrases, mustInclude } = searchCriteria;
  const colors = [
    'border-b-2 border-red-400 text-red-700',
    'border-b-2 border-blue-400 text-blue-700',
    'border-b-2 border-green-400 text-green-700',
    'border-b-2 border-purple-400 text-purple-700',
  ];

  exactPhrases.forEach((_, i) => {
    out = out.replace(
      new RegExp(`(${exactRegexes[i].source})`, 'gi'),
      '<span class="bg-yellow-200 font-bold text-black">$1</span>'
    );
  });

  mustInclude.forEach((_, i) => {
    out = out.replace(
      new RegExp(`(${includeRegexes[i].source})`, 'gi'),
      `<span class="${colors[i % colors.length]} font-bold">$1</span>`
    );
  });

  if (!exactPhrases.length && !mustInclude.length) {
    orRegexes.forEach(rx => {
      out = out.replace(
        new RegExp(`(${rx.source})`, 'gi'),
        '<span class="bg-yellow-200 font-bold text-black">$1</span>'
      );
    });
  }

  return out;
};

// --- Toast ---

const Toast = ({ notification, onClose, isMobile }) => {
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(onClose, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification, onClose]);

  if (!notification) return null;

  const typeClass = {
    success: 'bg-green-500 text-white',
    error:   'bg-red-500 text-white',
    warning: 'bg-yellow-500 text-white',
  }[notification.type] ?? 'bg-blue-500 text-white';

  const style = isMobile
    ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999, maxWidth: '90vw' }
    : { position: 'fixed', top: '16px', right: '16px', zIndex: 9999, maxWidth: '384px' };

  return (
    <div style={style} className={`px-4 py-3 rounded-lg shadow-xl transition-all duration-300 ${typeClass}`}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium leading-relaxed">{notification.message}</span>
        <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100 flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// --- App principal ---

const App = () => {
  const [query, setQuery]                     = useState('');
  const [results, setResults]                 = useState([]);
  const [isSearching, setIsSearching]         = useState(false);
  const [searchHistory, setSearchHistory]     = useState([]);
  const [recentFiles, setRecentFiles]         = useState([]);
  const [progress, setProgress]               = useState(0);
  const [status, setStatus]                   = useState('Listo para buscar');
  const [showInfo, setShowInfo]               = useState(false);
  const [showHistory, setShowHistory]         = useState(false);
  const [showRecent, setShowRecent]           = useState(false);
  const [activeTab, setActiveTab]             = useState('search');
  const [currentResultsCount, setCurrentResultsCount] = useState(0);
  const [indexLoading, setIndexLoading]       = useState(true);
  const [indexError, setIndexError]           = useState(null);
  const [searchIndex, setSearchIndex]         = useState({});
  const [darkMode, setDarkMode]               = useState(false);
  const [notification, setNotification]       = useState(null);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [isMobile, setIsMobile]               = useState(false);
  const [isPWA, setIsPWA]                     = useState(false);
  const [isOffline, setIsOffline]             = useState(!navigator.onLine);
  const [swReady, setSwReady]                 = useState(false);
  const [showOfflineInfo, setShowOfflineInfo] = useState(true);
  const [deferredPrompt, setDeferredPrompt]   = useState(null);
  const [canInstall, setCanInstall]           = useState(false);
  const [mobilePageModal, setMobilePageModal] = useState(null); // { filename, page }

  const searchInputRef    = useRef(null);
  const abortControllerRef = useRef(null);

  const showToast = useCallback((message, type = 'info') => {
    setNotification({ message, type, id: Date.now() });
  }, []);

  const dismissOfflineInfo = useCallback(() => {
    setShowOfflineInfo(false);
    try { localStorage.setItem('prometeo-offline-info-shown', 'true'); } catch {}
  }, []);

  const openOfflineDownload = useCallback(() => {
    showToast('Funcionalidad de descarga offline próximamente disponible', 'info');
    dismissOfflineInfo();
  }, [showToast, dismissOfflineInfo]);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) {
      showToast(
        isMobile
          ? 'Para instalar: Menu → "Agregar a pantalla de inicio"'
          : 'Para instalar: Busca el ícono de instalación en la barra de direcciones',
        'info'
      );
      return;
    }
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      showToast(outcome === 'accepted' ? 'Instalando aplicación...' : 'Instalación cancelada',
                outcome === 'accepted' ? 'success' : 'info');
      setDeferredPrompt(null);
      setCanInstall(false);
    } catch {
      showToast('Error durante la instalación', 'error');
    }
  }, [deferredPrompt, isMobile, showToast]);

  // Detectar móvil
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
      showToast('App disponible para instalar', 'info');
    };
    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setCanInstall(false);
      showToast('¡App instalada exitosamente!', 'success');
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    if (window.deferredPrompt) { setDeferredPrompt(window.deferredPrompt); setCanInstall(true); }
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [showToast]);

  // Service Worker + online/offline
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true;
    setIsPWA(isStandalone);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          navigator.serviceWorker.addEventListener('message', ({ data }) => {
            if (data.type === 'SW_READY')      { setSwReady(true); showToast('Búsquedas offline disponibles', 'success'); }
            if (data.type === 'INDICES_CACHED') showToast('Índices descargados. ¡Ya puedes buscar sin internet!', 'success');
          });
        })
        .catch(err => console.warn('SW error:', err));
    }

    const handleOnline  = () => { setIsOffline(false); showToast('Conexión restablecida', 'success'); };
    const handleOffline = () => { setIsOffline(true);  showToast('Sin conexión - Modo offline activado', 'warning'); };
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showToast]);

  // Cargar preferencias
  useEffect(() => {
    try {
      if (localStorage.getItem('prometeo-theme') === 'dark') setDarkMode(true);
      if (localStorage.getItem('prometeo-offline-info-shown') === 'true') setShowOfflineInfo(false);
    } catch {}
  }, []);

  // Aplicar tema
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    try { localStorage.setItem('prometeo-theme', darkMode ? 'dark' : 'light'); } catch {}
  }, [darkMode]);

  // Atajos de teclado (solo desktop)
  useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        if (e.key === 'Escape') e.target.blur();
        return;
      }
      if (e.key === '/') { e.preventDefault(); searchInputRef.current?.focus(); showToast('Modo búsqueda activado', 'info'); }
      if (e.key === 'Escape') { setShowInfo(false); setShowKeyboardHelp(false); setShowHistory(false); setShowRecent(false); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); searchInputRef.current?.focus(); showToast('Búsqueda enfocada', 'info'); }
      if ((e.ctrlKey || e.metaKey) && e.key === '/') { e.preventDefault(); setShowKeyboardHelp(true); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, showToast]);

  // Cargar índice por chunks
  const loadSearchIndex = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = 1000 * Math.pow(2, retryCount);

    try {
      setIndexLoading(true);
      setIndexError(null);
      setProgress(0);
      setStatus(`Cargando configuración...${retryCount > 0 ? ` (Intento ${retryCount + 1})` : ''}`);

      const manifestRes = await fetch('/index-manifest.json', { cache: 'force-cache', headers: { Accept: 'application/json' } });
      if (!manifestRes.ok) throw new Error(`Error cargando manifest: ${manifestRes.status}`);
      const manifest = await manifestRes.json();
      if (!manifest.chunks || !Array.isArray(manifest.chunks)) throw new Error('Manifest inválido');

      setStatus(`Encontrados ${manifest.totalFiles} archivos en ${manifest.totalChunks} chunks`);

      const loadedIndex = {};
      let loadedFiles = 0;
      const totalChunks = manifest.chunks.length;

      for (let i = 0; i < totalChunks; i++) {
        const chunkName = manifest.chunks[i];
        try {
          const res = await fetch(`/index-chunks/${chunkName}`, { cache: 'force-cache', headers: { Accept: 'application/json' } });
          if (res.ok) {
            const data = await res.json();
            if (data && typeof data === 'object') {
              // Object.assign evita crear una copia nueva del objeto completo en cada iteración
              Object.assign(loadedIndex, data);
              loadedFiles += Object.keys(data).length;
            }
          }
        } catch (e) {
          console.warn(`Error en chunk ${chunkName}:`, e.message);
        }

        setProgress(Math.round(((i + 1) / totalChunks) * 100));
        // Yield mínimo para mantener UI responsiva sin agregar latencia artificial
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      if (loadedFiles === 0) throw new Error('No se pudo cargar ningún archivo del índice');

      setSearchIndex(loadedIndex);
      setIndexLoading(false);
      setProgress(100);

      const successRate = Math.round((loadedFiles / manifest.totalFiles) * 100);
      if (successRate === 100) {
        setStatus(`✅ Índice cargado: ${loadedFiles} archivos disponibles`);
        showToast(`Índice cargado: ${loadedFiles} archivos disponibles`, 'success');
      } else {
        setStatus(`⚠️ Índice parcial: ${loadedFiles}/${manifest.totalFiles} archivos`);
        showToast(`Carga parcial: ${loadedFiles}/${manifest.totalFiles} archivos`, 'warning');
      }

    } catch (error) {
      if (error.name === 'AbortError') return;

      if (retryCount < maxRetries) {
        setStatus(`Error cargando índice. Reintentando en ${retryDelay / 1000}s...`);
        showToast('Error cargando índice. Reintentando...', 'error');
        setTimeout(() => loadSearchIndex(retryCount + 1), retryDelay);
      } else {
        try {
          const fallback = await fetch('/index.json', { cache: 'force-cache' });
          if (fallback.ok) {
            setSearchIndex(await fallback.json());
            setIndexLoading(false);
            setStatus('✅ Índice de respaldo cargado');
            showToast('Índice de respaldo cargado', 'warning');
            return;
          }
        } catch {}
        setIndexError(`No se pudo cargar el índice: ${error.message}`);
        setIndexLoading(false);
        setStatus('❌ Error al cargar el índice');
        showToast('Error crítico al cargar el índice', 'error');
      }
    }
  }, [showToast]);

  useEffect(() => {
    loadSearchIndex();
    try {
      const h = localStorage.getItem('prometeo-search-history');
      const r = localStorage.getItem('prometeo-recent-files');
      if (h) setSearchHistory(JSON.parse(h));
      if (r) setRecentFiles(JSON.parse(r));
    } catch {}
    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, [loadSearchIndex]);

  useEffect(() => {
    try { localStorage.setItem('prometeo-search-history', JSON.stringify(searchHistory)); } catch {}
  }, [searchHistory]);

  useEffect(() => {
    try { localStorage.setItem('prometeo-recent-files', JSON.stringify(recentFiles)); } catch {}
  }, [recentFiles]);

  const addToRecentFiles = useCallback((filename) => {
    setRecentFiles(prev => [filename, ...prev.filter(f => f !== filename)].slice(0, 5));
  }, []);

  const getSnippet = useCallback((text, compiled, searchCriteria) => {
    const paragraphs = text.split('\n\n');
    const matchingIndex = paragraphs.findIndex(p => matchesFast(p, compiled));
    if (matchingIndex === -1) return '';
    const slice = paragraphs.slice(Math.max(matchingIndex - 1, 0), Math.min(matchingIndex + 2, paragraphs.length));
    return highlightFast(slice.join('\n\n'), compiled, searchCriteria);
  }, []);

  const performSearch = useCallback(async () => {
    let trimmedQuery = query.trim();
    if (!trimmedQuery) { showToast('Por favor, ingrese un término de búsqueda', 'warning'); return; }
    if (indexLoading)  { showToast('Esperando a que se cargue el índice...', 'warning'); return; }
    if (indexError)    { showToast('Error en el índice. Recargue la página', 'error'); return; }
    if (Object.keys(searchIndex).length === 0) { showToast('El índice de búsqueda está vacío', 'error'); return; }

    // Si el usuario escribe múltiples palabras sin comillas ni operadores (+/-),
    // se trata automáticamente como frase exacta para dar resultados precisos.
    const words = trimmedQuery.split(/\s+/);
    const hasQuotes    = trimmedQuery.includes('"');
    const hasOperators = /[+\-]/.test(trimmedQuery);
    if (words.length > 1 && !hasQuotes && !hasOperators) {
      trimmedQuery = `"${trimmedQuery}"`;
      setQuery(trimmedQuery);
      showToast('Búsqueda como frase exacta — agregá + o - para búsquedas avanzadas', 'info');
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setIsSearching(true);
    setProgress(0);
    setStatus('Buscando...');
    setResults([]);

    setSearchHistory(prev => prev.includes(trimmedQuery) ? prev : [trimmedQuery, ...prev.slice(0, 9)]);

    try {
      const searchCriteria = parseSearchQuery(trimmedQuery);
      // Compilar patrones UNA VEZ antes del loop (evita ~63.000 compilaciones)
      const compiled = compilePatterns(searchCriteria);
      const foundResults = [];

      const files = Object.keys(searchIndex);
      const totalFiles = files.length;
      const batchSize = 10;
      const progressStep = Math.max(1, Math.floor(totalFiles / 10)); // actualizar status cada ~10%

      for (let i = 0; i < totalFiles; i += batchSize) {
        if (abortControllerRef.current?.signal.aborted) return;

        const batch = files.slice(i, i + batchSize);
        for (const filename of batch) {
          const filePages = searchIndex[filename];
          if (!filePages || typeof filePages !== 'object') continue;

          for (const [pageNum, pageText] of Object.entries(filePages)) {
            if (!pageText || typeof pageText !== 'string') continue;
            if (matchesFast(pageText, compiled)) {
              const snippet = getSnippet(pageText, compiled, searchCriteria);
              if (snippet) foundResults.push({ filename, page: parseInt(pageNum), snippet });
            }
          }
        }

        const pct = Math.min(Math.round(((i + batchSize) / totalFiles) * 100), 100);
        setProgress(pct);
        // Actualizar texto solo en hitos del 25% para no disparar 165 re-renders
        if (i % (progressStep * 2.5) < batchSize) setStatus(`Buscando... ${pct}%`);

        // Yield mínimo para mantener UI responsiva
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      setResults(foundResults);
      setCurrentResultsCount(foundResults.length);
      setStatus(`Búsqueda terminada: ${foundResults.length} resultado(s) encontrado(s).`);

      if (foundResults.length > 0) {
        showToast(`${foundResults.length} coincidencias en ${new Set(foundResults.map(r => r.filename)).size} documentos`, 'success');
        setActiveTab('results');
      } else {
        showToast('No se encontraron resultados', 'info');
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        setStatus('Búsqueda cancelada');
        showToast('Búsqueda cancelada', 'info');
      } else {
        setStatus(`Error: ${error.message}`);
        showToast(`Error: ${error.message}`, 'error');
      }
    } finally {
      setIsSearching(false);
      setProgress(100);
    }
  }, [query, searchIndex, indexLoading, indexError, showToast, getSnippet]);

  // Agrupa resultados por documento, ordenados por mayor cantidad de coincidencias
  const groupedResults = useMemo(() => {
    const map = {};
    for (const r of results) {
      if (!map[r.filename]) map[r.filename] = { filename: r.filename, pages: [] };
      map[r.filename].pages.push({ page: r.page, snippet: r.snippet });
    }
    return Object.values(map).sort((a, b) => b.pages.length - a.pages.length);
  }, [results]);

  const clearSearch = useCallback(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setQuery('');
    setResults([]);
    setStatus(indexLoading ? 'Cargando índice...' : 'Listo para buscar');
    setProgress(0);
    setCurrentResultsCount(0);
    setIsSearching(false);
    showToast('Búsqueda limpiada', 'info');
  }, [indexLoading, showToast]);

  const openFile = useCallback((filename, page) => {
    if (isOffline) {
      showToast('Sin conexión: no se pueden abrir PDFs sin internet', 'warning');
      return;
    }
    addToRecentFiles(filename);
    // En móvil, #page=N es ignorado por los visores nativos de iOS y Android.
    // Mostramos un panel con la página destino para que el usuario pueda navegar manualmente.
    if (isMobile) {
      setMobilePageModal({ filename, page });
      return;
    }
    try {
      const url = `/pdfs/${encodeURIComponent(filename)}#page=${page + 1}`;
      const w = window.open(url, '_blank');
      if (!w) showToast('No se pudo abrir el PDF. Permití los popups en este sitio', 'error');
      else showToast(`Abriendo ${filename}`, 'success');
    } catch {
      showToast('Error abriendo el archivo', 'error');
    }
  }, [addToRecentFiles, isOffline, isMobile, showToast]);

  const exportResults = useCallback(() => {
    if (!results.length) { showToast('No hay resultados para exportar', 'warning'); return; }
    try {
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
        <title>Resultados - ${query}</title>
        <style>body{font-family:Arial,sans-serif;margin:20px}h1{color:#2563eb;text-align:center}
        .result{margin:20px 0;padding:15px;border:1px solid #e5e7eb;border-radius:8px}
        .filename{font-weight:bold;color:#1f2937}.page{color:#6b7280}
        .snippet{margin-top:10px;background:#f9fafb;padding:10px;border-radius:4px}</style>
      </head><body>
        <h1>Resultados de Búsqueda</h1>
        <p><strong>Término:</strong> ${query}</p>
        <p><strong>Total:</strong> ${results.length} resultados en ${groupedResults.length} documentos</p>
        <p><strong>Fecha:</strong> ${new Date().toLocaleString()}</p>
        ${results.map(r => `<div class="result">
          <div class="filename">${r.filename}</div>
          <div class="page">Página ${r.page + 1}</div>
          <div class="snippet">${r.snippet}</div>
        </div>`).join('')}
      </body></html>`;
      const a = Object.assign(document.createElement('a'), {
        href: URL.createObjectURL(new Blob([html], { type: 'text/html' })),
        download: `${query.replace(/[<>:"/\\|?*]/g, '')}_resultados.html`,
      });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      showToast('Resultados exportados', 'success');
    } catch {
      showToast('Error al exportar', 'error');
    }
  }, [results, groupedResults, query, showToast]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !isSearching && !indexLoading) performSearch();
  }, [performSearch, isSearching, indexLoading]);

  const retryLoadIndex = useCallback(() => {
    setIndexError(null);
    loadSearchIndex();
  }, [loadSearchIndex]);

  // Clases de tema
  const theme = useMemo(() => ({
    background: darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-white'  : 'bg-gray-50 text-gray-900',
    card:       darkMode ? 'bg-gray-800 border border-gray-700 text-white'            : 'bg-white border border-gray-200 text-gray-900',
    cardHover:  darkMode ? 'hover:border-gray-500 hover:shadow-lg'                    : 'hover:border-blue-200 hover:shadow-md',
    button:     darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white'                 : 'bg-gray-100 hover:bg-gray-200 text-gray-700',
    input:      darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
    text:       darkMode ? 'text-gray-300' : 'text-gray-500',
    textStrong: darkMode ? 'text-gray-100' : 'text-gray-800',
    accent:     darkMode ? 'text-blue-300' : 'text-blue-600',
    chip:       darkMode ? 'bg-gray-700 hover:bg-blue-700 text-gray-200 border border-gray-600 hover:border-blue-500'
                         : 'bg-blue-50 hover:bg-blue-600 text-blue-700 hover:text-white border border-blue-200 hover:border-blue-600',
  }), [darkMode]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme.background}`}>
      <Toast notification={notification} onClose={() => setNotification(null)} isMobile={isMobile} />

      <div className="container mx-auto px-4 py-6 max-w-4xl">

        {/* ── Header ── */}
        <header className="mb-8">
          {/* Controles superiores */}
          <div className="flex justify-end mb-3 gap-2">
            {canInstall && !isPWA && (
              <button onClick={handleInstallClick}
                className={`p-2 rounded-lg transition-all ${theme.button} hover:scale-105 relative`}
                title="Instalar aplicación">
                <Download className="w-4 h-4" />
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              </button>
            )}
            <button onClick={() => setDarkMode(d => !d)}
              className={`p-2 rounded-lg transition-all ${theme.button} hover:scale-105`}
              title={`Modo ${darkMode ? 'claro' : 'oscuro'}`}>
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {!isMobile && (
              <button onClick={() => setShowKeyboardHelp(true)}
                className={`p-2 rounded-lg transition-all ${theme.button} hover:scale-105`}
                title="Atajos de teclado (Ctrl+/)">
                <Keyboard className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Logo + título */}
          <div className="flex items-center justify-center gap-4">
            <img src="/logo_app.svg" alt="Prometeo"
              className={`object-contain transition-transform hover:scale-105 ${isMobile ? 'w-12 h-12' : 'w-16 h-16'}`}
              onError={e => { e.target.style.display = 'none'; }} />
            <div className="text-left">
              <h1 className={`font-bold leading-tight ${isMobile ? 'text-2xl' : 'text-3xl'} ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                PROMETEO
              </h1>
              <p className={`${isMobile ? 'text-sm' : 'text-base'} ${theme.accent} font-medium`}>
                Buscador Digital Gnóstico
              </p>
            </div>
          </div>
        </header>

        {/* ── Alertas ── */}
        {indexError && (
          <div className={`rounded-xl p-4 mb-5 flex items-center gap-3 ${darkMode ? 'bg-red-900/60 border border-red-700 text-red-100' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-sm">Error cargando el índice</p>
              <p className="text-xs mt-0.5 opacity-80">{indexError}</p>
            </div>
            <button onClick={retryLoadIndex} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors">
              Reintentar
            </button>
          </div>
        )}

        {indexLoading && (
          <div className={`rounded-xl p-4 mb-5 flex items-center gap-3 ${darkMode ? 'bg-blue-900/60 border border-blue-700 text-blue-100' : 'bg-blue-50 border border-blue-200 text-blue-800'}`}>
            <Loader className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-sm">Cargando índice de búsqueda</p>
              {progress > 0 && (
                <div className={`mt-2 w-full rounded-full h-1.5 ${darkMode ? 'bg-blue-800' : 'bg-blue-200'}`}>
                  <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>
          </div>
        )}

        {showOfflineInfo && swReady && (
          <div className={`rounded-xl p-4 mb-5 ${darkMode ? 'bg-gradient-to-r from-purple-900/60 to-blue-900/60 border border-purple-700 text-purple-100' : 'bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 text-purple-800'}`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl flex-shrink-0">📚</span>
              <div className="flex-1">
                <p className="font-semibold text-sm mb-1">¿Querés acceder sin internet?</p>
                <p className={`text-xs mb-3 ${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
                  Descargá la biblioteca completa (~850 MB). Recomendamos usar WiFi.
                </p>
                <div className="flex gap-2">
                  <button onClick={openOfflineDownload}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105 ${darkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-600 hover:bg-purple-700 text-white'}`}>
                    📥 Descargar
                  </button>
                  <button onClick={dismissOfflineInfo}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${theme.button}`}>
                    Ahora no
                  </button>
                </div>
              </div>
              <button onClick={dismissOfflineInfo} className={`p-1 rounded transition-colors ${theme.text} hover:opacity-70`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {isOffline && (
          <div className={`rounded-xl p-3 mb-5 flex items-center justify-center gap-2 ${darkMode ? 'bg-yellow-900/60 border border-yellow-700 text-yellow-100' : 'bg-yellow-50 border border-yellow-200 text-yellow-800'}`}>
            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium">Modo offline — las búsquedas funcionan con los índices descargados</span>
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="flex gap-1 mb-5 p-1 rounded-xl shadow-sm w-full max-w-sm mx-auto"
             style={{ background: darkMode ? '#1f2937' : '#f3f4f6' }}>
          {[
            { id: 'search',  label: 'Búsqueda',       icon: Search },
            { id: 'results', label: `Resultados${groupedResults.length > 0 ? ` (${groupedResults.length})` : ''}`, icon: FileText },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg font-medium transition-all duration-200 ${
                activeTab === id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : `${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
              }`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* ── Pestaña Búsqueda ── */}
        {activeTab === 'search' && (
          <div className={`rounded-2xl shadow-sm p-5 mb-5 transition-colors ${theme.card}`}>
            {/* Input */}
            <div className="flex gap-2 mb-4">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.text}`} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isMobile ? 'Buscar...' : 'Buscar... ("frase exacta" +obligatoria -excluida)'}
                  className={`w-full pl-9 pr-9 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm disabled:opacity-50 ${theme.input}`}
                  disabled={isSearching || indexLoading}
                />
                {query && (
                  <button onClick={() => setQuery('')} disabled={isSearching}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-opacity ${theme.text} hover:opacity-60`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <button onClick={performSearch}
                disabled={isSearching || !query.trim() || indexLoading || !!indexError}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-medium text-sm shadow-sm hover:shadow-md">
                {isSearching ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="hidden sm:inline">Buscar</span>
              </button>
            </div>

            {/* Acciones secundarias */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { icon: Clock,    label: 'Historial', action: () => setShowHistory(h => !h), active: showHistory },
                { icon: FileText, label: 'Recientes',  action: () => setShowRecent(r => !r),  active: showRecent },
                { icon: RotateCcw, label: 'Limpiar',  action: clearSearch,                    active: false },
                { icon: Info,     label: 'Ayuda',     action: () => setShowInfo(true),        active: showInfo },
              ].map(({ icon: Icon, label, action, active }) => (
                <button key={label} onClick={action} disabled={isSearching && label === 'Limpiar' ? false : (isSearching && label !== 'Ayuda')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
                    active
                      ? (darkMode ? 'bg-blue-700 text-white' : 'bg-blue-100 text-blue-700')
                      : `${darkMode ? 'text-blue-300 bg-blue-900/40 hover:bg-blue-800/60' : 'text-blue-600 bg-blue-50 hover:bg-blue-100'}`
                  }`}>
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {/* Historial */}
            {showHistory && searchHistory.length > 0 && (
              <div className={`mb-4 p-3 rounded-xl ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme.text}`}>Historial</p>
                <div className="flex flex-wrap gap-1.5">
                  {searchHistory.map((term, i) => (
                    <button key={i} onClick={() => setQuery(term)} disabled={isSearching}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                        darkMode ? 'bg-gray-600 border-gray-500 hover:bg-gray-500 text-gray-200' : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                      }`}>
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Archivos recientes */}
            {showRecent && recentFiles.length > 0 && (
              <div className={`mb-4 p-3 rounded-xl ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>
                <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme.text}`}>Abiertos recientemente</p>
                <div className="space-y-1">
                  {recentFiles.map((filename, i) => (
                    <button key={i} onClick={() => openFile(filename, 0)}
                      className={`w-full text-left px-3 py-2 text-xs rounded-lg border transition-all flex items-center gap-2 ${
                        darkMode ? 'bg-gray-600 border-gray-500 hover:bg-gray-500 text-gray-200' : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                      }`}>
                      <FileText className="w-3 h-3 flex-shrink-0 opacity-50" />
                      {filename}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Barra de progreso durante búsqueda */}
            {isSearching && (
              <div className="mb-3">
                <div className={`w-full rounded-full h-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div className="bg-blue-500 h-1.5 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}

            {/* Status */}
            <p className={`text-xs text-center ${isSearching ? 'text-blue-500' : indexError ? 'text-red-500' : theme.text}`}>
              {status}
            </p>
          </div>
        )}

        {/* ── Pestaña Resultados ── */}
        {activeTab === 'results' && (
          <div className={`rounded-2xl shadow-sm p-5 transition-colors ${theme.card}`}>
            {/* Cabecera de resultados */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
              <div>
                <h2 className={`text-base font-semibold ${theme.textStrong}`}>
                  {query ? (
                    <>Resultados para <span className="text-blue-600">"{query}"</span></>
                  ) : 'Resultados'}
                </h2>
                {groupedResults.length > 0 && (
                  <p className={`text-xs mt-0.5 ${theme.text}`}>
                    {groupedResults.length} {groupedResults.length === 1 ? 'documento' : 'documentos'} · {currentResultsCount} {currentResultsCount === 1 ? 'coincidencia' : 'coincidencias'}
                  </p>
                )}
              </div>
              {results.length > 0 && (
                <button onClick={exportResults}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-all hover:shadow-md">
                  <Download className="w-3.5 h-3.5" />
                  Exportar
                </button>
              )}
            </div>

            {/* Estado vacío */}
            {groupedResults.length === 0 ? (
              <div className="text-center py-16">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <Search className={`w-8 h-8 ${darkMode ? 'text-gray-500' : 'text-gray-300'}`} />
                </div>
                <p className={`font-medium mb-1 ${theme.textStrong}`}>
                  {results.length === 0 && !query ? 'Realizá una búsqueda' : 'Sin resultados'}
                </p>
                <p className={`text-xs ${theme.text}`}>
                  {query
                    ? 'Intentá con otros términos, sin acentos, o con comillas para frases exactas'
                    : 'Escribí un término en la pestaña Búsqueda'}
                </p>
                {query && (
                  <button onClick={() => setActiveTab('search')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                    Volver a buscar
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {groupedResults.map((group, idx) => (
                  <ResultCard key={idx} group={group} onOpen={openFile} theme={theme} darkMode={darkMode} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Modal Atajos ── */}
        {showKeyboardHelp && !isMobile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowKeyboardHelp(false)}>
            <div className={`rounded-2xl p-6 max-w-sm w-full shadow-2xl ${theme.card}`} onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-5">
                <h3 className={`font-semibold flex items-center gap-2 ${theme.textStrong}`}>
                  <Keyboard className="w-4 h-4" /> Atajos de teclado
                </h3>
                <button onClick={() => setShowKeyboardHelp(false)} className={theme.text}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                {[
                  ['/','Enfocar búsqueda'],
                  ['Ctrl + K','Búsqueda rápida'],
                  ['Escape','Cerrar modales'],
                  ['Ctrl + /','Mostrar esta ayuda'],
                ].map(([key, desc]) => (
                  <div key={key} className="flex justify-between items-center">
                    <span className={`text-sm ${theme.text}`}>{desc}</span>
                    <kbd className={`px-2 py-1 text-xs rounded-lg font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{key}</kbd>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Modal Info ── */}
        {showInfo && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowInfo(false)}>
            <div className={`rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl ${theme.card}`} onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-5">
                <h3 className={`font-semibold ${theme.textStrong}`}>Acerca de PROMETEO</h3>
                <button onClick={() => setShowInfo(false)} className={theme.text}><X className="w-5 h-5" /></button>
              </div>
              <div className={`space-y-4 text-sm ${theme.text}`}>
                <p><strong className={theme.textStrong}>Versión:</strong> 2.3 {isPWA && '· PWA'}</p>
                <p>Buscador de texto en múltiples libros gnósticos en formato PDF. Busca simultáneamente en todos los documentos del índice.</p>

                {(swReady || isPWA || canInstall) && (
                  <div className={`p-3 rounded-xl ${darkMode ? 'bg-green-900/40 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                    <p className={`font-semibold mb-2 text-xs uppercase tracking-wide ${darkMode ? 'text-green-300' : 'text-green-700'}`}>Estado de la app</p>
                    <ul className="space-y-1 text-xs">
                      {isPWA    && <li>✅ Instalada como app nativa</li>}
                      {swReady  && <li>✅ Búsquedas offline disponibles</li>}
                      {canInstall && !isPWA && <li>🔽 Disponible para instalar</li>}
                      <li>{isOffline ? '📵' : '🌐'} {isOffline ? 'Offline' : 'Online'}</li>
                    </ul>
                  </div>
                )}

                <div className={`p-3 rounded-xl ${darkMode ? 'bg-blue-900/40 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={`font-semibold mb-2 text-xs uppercase tracking-wide ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Sintaxis de búsqueda</p>
                  <ul className="space-y-1.5 text-xs">
                    {[
                      ['"frase exacta"', 'Busca la frase tal cual'],
                      ['+palabra', 'Término obligatorio'],
                      ['-palabra', 'Término excluido'],
                      ['palabra1 palabra2', 'Cualquiera de los dos (OR)'],
                    ].map(([code, desc]) => (
                      <li key={code} className="flex items-center gap-2">
                        <code className={`px-1.5 py-0.5 rounded font-mono text-xs ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{code}</code>
                        <span className={theme.text}>{desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`p-3 rounded-xl ${darkMode ? 'bg-yellow-900/40 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <p className={`font-semibold mb-2 text-xs uppercase tracking-wide ${darkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>Consejos</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Los acentos se ignoran automáticamente</li>
                    <li>• Usá términos específicos para mejores resultados</li>
                    <li>• Los resultados se ordenan por relevancia (más coincidencias primero)</li>
                    {!isMobile && <li>• Presioná <kbd className={`px-1 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>/</kbd> para enfocar la búsqueda rápidamente</li>}
                  </ul>
                </div>

                <p className={`text-xs ${theme.text}`}><strong className={theme.textStrong}>Contacto:</strong> consultoriotic@gmail.com</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal página en móvil ── */}
      {mobilePageModal && (
        <div className="fixed inset-0 bg-black/60 flex items-end justify-center z-50 p-0"
             onClick={() => setMobilePageModal(null)}>
          <div
            className={`w-full rounded-t-2xl p-6 shadow-2xl ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Tirador visual */}
            <div className={`w-10 h-1 rounded-full mx-auto mb-5 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />

            <div className="flex items-start gap-3 mb-5">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                <FileText className={`w-5 h-5 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs mb-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Documento</p>
                <p className="font-semibold text-sm leading-snug truncate">{mobilePageModal.filename}</p>
              </div>
            </div>

            {/* Página destacada */}
            <div className={`rounded-xl p-4 mb-5 text-center ${darkMode ? 'bg-blue-900/50 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
              <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`}>
                La coincidencia está en
              </p>
              <p className={`text-4xl font-bold ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Página {mobilePageModal.page + 1}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Anotala antes de abrir el PDF
              </p>
            </div>

            <p className={`text-xs text-center mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Los visores de PDF en móvil no navegan automáticamente a la página. Abrí el PDF y andá a la página indicada.
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => setMobilePageModal(null)}
                className={`flex-1 py-3 rounded-xl text-sm font-medium transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                Cancelar
              </button>
              <button
                onClick={() => {
                  const { filename, page } = mobilePageModal;
                  setMobilePageModal(null);
                  try {
                    const url = `/pdfs/${encodeURIComponent(filename)}#page=${page + 1}`;
                    const w = window.open(url, '_blank');
                    if (!w) showToast('No se pudo abrir el PDF. Permití los popups', 'error');
                  } catch {
                    showToast('Error abriendo el archivo', 'error');
                  }
                }}
                className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors shadow-sm">
                Abrir PDF →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <footer className={`text-center py-6 border-t mt-4 ${darkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-100 bg-white'}`}>
        <div className="flex flex-col items-center gap-2">
          <img src="/gnosis-logo.svg" alt="Gnosis Argentina"
            className="w-12 h-12 object-contain hover:scale-105 transition-transform"
            onError={e => { e.target.style.display = 'none'; }} />
          <p className={`text-xs ${theme.text}`}>© {new Date().getFullYear()} Gnosis Argentina</p>
        </div>
      </footer>
    </div>
  );
};

// --- Tarjeta de resultado agrupado ---

const ResultCard = ({ group, onOpen, theme, darkMode }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMultiple = group.pages.length > 1;

  // Intensidad del borde izquierdo según cantidad de coincidencias
  const borderColor =
    group.pages.length >= 8 ? 'border-l-red-500' :
    group.pages.length >= 4 ? 'border-l-orange-400' :
    group.pages.length >= 2 ? 'border-l-blue-400' :
                               'border-l-gray-300';

  return (
    <div className={`rounded-xl border-l-4 p-4 transition-all duration-200 ${borderColor} ${theme.card} ${theme.cardHover}`}>
      {/* Cabecera de la tarjeta */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <button
          onClick={() => onOpen(group.filename, group.pages[0].page)}
          className={`font-semibold text-sm text-left hover:text-blue-500 transition-colors flex-1 leading-snug ${theme.textStrong}`}
          title={`Abrir ${group.filename}`}>
          {group.filename}
        </button>
        <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
          group.pages.length >= 4
            ? (darkMode ? 'bg-orange-900/60 text-orange-300' : 'bg-orange-100 text-orange-700')
            : (darkMode ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-700')
        }`}>
          {group.pages.length} {group.pages.length === 1 ? 'página' : 'páginas'}
        </span>
      </div>

      {/* Chips de páginas */}
      {hasMultiple && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {group.pages.map(({ page }) => (
            <button key={page} onClick={() => onOpen(group.filename, page)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all hover:scale-105 ${theme.chip}`}>
              p. {page + 1}
            </button>
          ))}
        </div>
      )}

      {/* Snippet */}
      <div
        className={`text-xs leading-relaxed p-3 rounded-lg border-l-2 border-blue-300 ${
          darkMode ? 'text-gray-300 bg-gray-700/60' : 'text-gray-600 bg-gray-50'
        } ${hasMultiple && !expanded ? 'line-clamp-4' : ''}`}
        dangerouslySetInnerHTML={{ __html: group.pages[0].snippet }}
      />

      {/* Botón expandir si hay múltiples páginas */}
      {hasMultiple && (
        <button
          onClick={() => setExpanded(e => !e)}
          className={`mt-2 flex items-center gap-1 text-xs transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}>
          {expanded ? 'Mostrar menos' : `Ver fragmento completo`}
          <span className="text-xs">{expanded ? '▲' : '▼'}</span>
        </button>
      )}
    </div>
  );
};

export default App;
